/*!
 * EasyPets · Puppy Startpakket quiz
 *
 * Reads canonical config from window.EP_DISCRIMINATORS (rendered by the
 * `easypets-discriminators` snippet) and powers a 6-step quiz that writes
 * paspoort-conform `pet_*` keys to Klaviyo + Shopify cart attributes, so
 * the lead enters every downstream flow (belteam, voer-transitie,
 * weeg-herinnering) with the same field names the dashboard expects.
 */
(function () {
  'use strict';

  const D = window.EP_DISCRIMINATORS;
  if (!D) {
    console.warn('[easypets-puppy-quiz] EP_DISCRIMINATORS not found, skipping init');
    return;
  }

  // ----- ras_lookup loader (async, cached) -----
  let rasLookupPromise = null;
  function loadRasLookup() {
    if (rasLookupPromise) return rasLookupPromise;
    rasLookupPromise = fetch(D.ras_lookup, { credentials: 'omit' })
      .then((r) => (r.ok ? r.json() : []))
      .catch(() => []);
    return rasLookupPromise;
  }

  // ----- field derivations -----
  function deriveSizeCategoryFromBucket(bucketKey) {
    const b = (D.quiz_size_buckets || []).find((x) => x.key === bucketKey);
    return b ? b.pet_size_category : null;
  }

  function deriveLevensfase(soort, geboortedatumIso) {
    const buckets = (D.levensfase || {})[soort];
    if (!buckets || !geboortedatumIso) return null;
    const months = Math.floor(
      (Date.now() - new Date(geboortedatumIso).getTime()) / (1000 * 60 * 60 * 24 * 30.4375)
    );
    for (const [key, b] of Object.entries(buckets)) {
      if (months >= b.maanden_min && (b.maanden_max === null || months < b.maanden_max)) {
        return key;
      }
    }
    return null;
  }

  function lookupRasMeta(rasNaam, rasLookup) {
    if (!rasNaam) return null;
    const target = rasNaam.toLowerCase().trim();
    return (rasLookup || []).find((r) => r.ras.toLowerCase() === target) || null;
  }

  // ----- phone normalisation to E.164 (NL/BE) -----
  function normalisePhoneNL(raw) {
    if (!raw) return '';
    const digits = String(raw).replace(/[^\d+]/g, '');
    if (digits.startsWith('+')) return digits;
    if (digits.startsWith('00')) return '+' + digits.slice(2);
    if (digits.startsWith('06')) return '+316' + digits.slice(2);
    if (digits.startsWith('0')) return '+31' + digits.slice(1);
    if (digits.startsWith('31')) return '+' + digits;
    if (digits.startsWith('32')) return '+' + digits;
    return digits;
  }
  function isValidEmail(e) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(e || '').trim());
  }
  function isValidPhone(p) {
    const n = normalisePhoneNL(p);
    return /^\+\d{9,15}$/.test(n);
  }

  // ----- Klaviyo glue -----
  function klaviyoReady() {
    return new Promise((resolve) => {
      if (window.klaviyo && typeof window.klaviyo.identify === 'function') return resolve(window.klaviyo);
      let tries = 0;
      const t = setInterval(() => {
        if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
          clearInterval(t);
          resolve(window.klaviyo);
        } else if (++tries > 40) {
          clearInterval(t);
          resolve(null);
        }
      }, 100);
    });
  }
  async function klaviyoIdentify(profile) {
    const k = await klaviyoReady();
    if (!k) return false;
    try {
      await k.identify(profile);
      return true;
    } catch (e) {
      console.warn('[easypets-puppy-quiz] klaviyo.identify failed', e);
      return false;
    }
  }
  async function klaviyoTrack(event, properties) {
    const k = await klaviyoReady();
    if (!k) return false;
    try {
      await k.track(event, properties);
      return true;
    } catch (e) {
      console.warn('[easypets-puppy-quiz] klaviyo.track failed', e);
      return false;
    }
  }

  // ----- Shopify add-to-cart + checkout redirect -----
  function buildShopifyCheckoutUrl(payload) {
    const variantId = D.shopify && D.shopify.puppy_startpakket_variant_id;
    if (!variantId || String(variantId).indexOf('REPLACE_WITH') === 0) {
      console.warn('[easypets-puppy-quiz] Shopify variant ID not configured — staying on success state');
      return null;
    }
    const params = new URLSearchParams();
    Object.keys(payload).forEach((k) => {
      const v = payload[k];
      if (v === null || v === undefined || v === '') return;
      params.append('attributes[' + k + ']', String(v));
    });
    params.append('checkout[email]', payload.email || '');
    if (payload.phone_number) {
      params.append('checkout[shipping_address][phone]', payload.phone_number);
    }
    params.append('return_to', D.shopify.cart_redirect || '/checkout');
    return '/cart/' + variantId + ':1?' + params.toString();
  }

  // ----- Init each quiz on the page -----
  function initQuiz(root) {
    if (!root || root.dataset.epQuizReady === '1') return;
    root.dataset.epQuizReady = '1';

    const TOTAL = 6;
    const state = {
      pet_ras: '',
      ep_size_bucket: null,
      pet_gewicht_huidig_kg: '',
      pet_geslacht: null,
      pet_naam: '',
      pet_geboortedatum: '',
      email: '',
      phone_number: '',
    };
    let current = 1;

    const steps = root.querySelectorAll('.ep-quiz-step');
    const dots = root.querySelectorAll('.ep-quiz-card__progress .step-dot');
    const stepNumEl = root.querySelector('[data-step-num]');

    function show(n) {
      current = n;
      steps.forEach((s) => s.classList.toggle('active', +s.dataset.step === n));
      dots.forEach((d, i) => {
        d.classList.remove('active', 'done');
        if (i + 1 < n) d.classList.add('done');
        else if (i + 1 === n) d.classList.add('active');
      });
      if (stepNumEl) stepNumEl.textContent = Math.min(n, TOTAL);
      const cta = root.querySelector('[data-pet-naam-cta]');
      if (cta) cta.textContent = state.pet_naam ? 'van ' + state.pet_naam : '';
    }

    // Option-button groups (size bucket + geslacht)
    root.querySelectorAll('.ep-quiz-options').forEach((group) => {
      const key = group.dataset.q;
      group.addEventListener('click', (e) => {
        const b = e.target.closest('button[data-value]');
        if (!b) return;
        e.preventDefault();
        group.querySelectorAll('button').forEach((x) => x.classList.remove('selected'));
        b.classList.add('selected');
        state[key] = b.dataset.value;
      });
    });

    // Text/number/date/email/tel inputs
    root.querySelectorAll('[data-field]').forEach((inp) => {
      inp.addEventListener('input', () => {
        state[inp.dataset.field] = inp.value;
      });
    });

    // Ras typeahead → auto-select size bucket
    const rasInput = root.querySelector('[data-field="pet_ras"]');
    const datalist = root.querySelector('#ep-ras-list');
    if (rasInput && datalist) {
      rasInput.addEventListener('input', () => {
        const opt = [...datalist.querySelectorAll('option')].find(
          (o) => o.value.toLowerCase() === rasInput.value.toLowerCase().trim()
        );
        if (opt) {
          const size = opt.dataset.size;
          const group = root.querySelector('.ep-quiz-options[data-q="ep_size_bucket"]');
          if (group) {
            group.querySelectorAll('button').forEach((x) => x.classList.remove('selected'));
            const target = group.querySelector('button[data-value="' + size + '"]');
            if (target) target.classList.add('selected');
            state.ep_size_bucket = size;
          }
        }
      });
    }

    // Per-step validation
    function validateStep(n) {
      if (n === 1 && !state.ep_size_bucket) return 'Kies het volwassen formaat van je pup.';
      if (n === 2 && (!state.pet_gewicht_huidig_kg || +state.pet_gewicht_huidig_kg <= 0))
        return 'Vul het gewicht van je pup in.';
      if (n === 3 && !state.pet_geslacht) return 'Kies reu of teef.';
      if (n === 4 && !state.pet_naam) return 'Vul de naam van je pup in.';
      if (n === 5 && !state.pet_geboortedatum) return 'Vul de geboortedatum in.';
      if (n === 6) {
        if (!isValidEmail(state.email)) return 'Vul een geldig e-mailadres in.';
        if (!isValidPhone(state.phone_number))
          return 'Vul een geldig telefoonnummer in (bijv. 06 12345678).';
      }
      return null;
    }

    root.querySelectorAll('[data-next]').forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        const err = validateStep(current);
        if (err) {
          alert(err);
          return;
        }
        await klaviyoTrack(D.klaviyo.events.quiz_step, {
          step: current,
          ep_size_bucket: state.ep_size_bucket,
          pet_engine_route: 'puppy',
        });
        show(Math.min(current + 1, TOTAL));
      });
    });
    root.querySelectorAll('[data-prev]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        show(Math.max(current - 1, 1));
      });
    });

    // Submit
    const submitBtn = root.querySelector('[data-submit]');
    if (submitBtn) {
      submitBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        const err = validateStep(TOTAL);
        if (err) {
          alert(err);
          return;
        }
        submitBtn.disabled = true;
        submitBtn.textContent = 'Even bezig…';

        const rasLookup = await loadRasLookup();
        const rasMeta = lookupRasMeta(state.pet_ras, rasLookup);
        const extras = D.landingspagina_payload_extras.puppy_startpakket;

        const payload = {
          // owner
          email: state.email.trim(),
          phone_number: normalisePhoneNL(state.phone_number),
          // pet (verplicht)
          pet_naam: state.pet_naam.trim(),
          pet_soort: extras.pet_soort,
          pet_geboortedatum: state.pet_geboortedatum,
          pet_geslacht: state.pet_geslacht,
          pet_gewicht_huidig_kg: parseFloat(state.pet_gewicht_huidig_kg),
          pet_ras: state.pet_ras.trim() || 'onbekend',
          // pet (auto-afgeleid hier, definitieve waardes komen uit Klaviyo)
          pet_size_category: rasMeta
            ? rasMeta.size_category
            : deriveSizeCategoryFromBucket(state.ep_size_bucket),
          pet_gewicht_doel_kg: rasMeta ? rasMeta.gewicht_doel_kg : null,
          pet_levensfase: deriveLevensfase(extras.pet_soort, state.pet_geboortedatum),
          // routing
          pet_engine_route: extras.pet_engine_route,
          puppy_startpakket: extras.puppy_startpakket,
          source: extras.source,
          // quiz raw
          ep_size_bucket: state.ep_size_bucket,
        };

        // Klaviyo: identify + track
        await klaviyoIdentify({
          email: payload.email,
          phone_number: payload.phone_number,
          properties: payload,
        });
        await klaviyoTrack(D.klaviyo.events.quiz_completed, payload);

        // Shopify: cart-add + redirect (or success state)
        const url = buildShopifyCheckoutUrl(payload);
        if (url) {
          window.location.href = url;
          return;
        }
        // No variant configured → show success state
        const nameEl = root.querySelector('[data-pet-naam]');
        if (nameEl && payload.pet_naam) nameEl.textContent = ' voor ' + payload.pet_naam;
        show(TOTAL + 1);
      });
    }

    // Fire quiz_started on first interaction
    let started = false;
    root.addEventListener(
      'click',
      function once() {
        if (started) return;
        started = true;
        klaviyoTrack(D.klaviyo.events.quiz_started, { source: 'landingpage_puppy_startpakket' });
      },
      { once: true }
    );
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-easypets-quiz="puppy-startpakket"]').forEach(initQuiz);
  });
})();
